import { ArchiveIcon, PlusIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonSelectInput } from 'components/common/form/select';
import { CommonTableButton } from 'components/common/table/button';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineModelsContext } from 'contexts/admin/machine-models.context';
import { CheckedContext } from 'contexts/layout/checked.context';
import {
  MachineModelsDialog,
  MachineModelsFilters,
} from 'interfaces/i-machine-models-table';
import {
  MachineModelType,
  ModelStatus,
} from 'lib_ts/enums/machine-models.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { useContext, useMemo, useState } from 'react';
import { AdminMachineModelsService } from 'services/admin/machine-models.service';

interface IProps {
  filters: MachineModelsFilters;
  updateFilters: (filters: Partial<MachineModelsFilters>) => void;
  openDialog: (dialog: MachineModelsDialog) => void;
}

export const MachineModelsToolbar = ({
  filters,
  updateFilters,
  openDialog,
}: IProps) => {
  const [archivingUnused, setArchivingUnused] = useState(false);
  const modelsCx = useContext(MachineModelsContext);
  const checkedCx = useContext(CheckedContext);

  const machines = useMachinesPortalStore((s) => s.machines);

  const machineOptions = useMemo(
    () =>
      machines.map((m) => ({
        label: m.machineID,
        value: m.machineID,
      })),
    [machines]
  );

  const onArchiveUnused = async () => {
    try {
      setArchivingUnused(true);
      checkedCx.resetChecked();

      await AdminMachineModelsService.getInstance().archiveUnused();
      modelsCx.refresh();
    } catch (error) {
      console.error(error);

      NotifyHelper.error({
        message_md: `Failed to archive unused models: error`,
      });
    } finally {
      setArchivingUnused(false);
    }
  };

  const boolOptions = useMemo(() => {
    const output: IOption[] = [
      {
        label: 'Yes',
        value: 'true',
      },
      {
        label: 'No',
        value: 'false',
      },
    ];

    return output;
  }, []);

  return (
    <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
      <Grid columns="7" gap={RADIX.FLEX.GAP.SM} flexGrow="1">
        <Box>
          <CommonSearchInput
            id="machine-models-names"
            placeholder="common.name"
            options={modelsCx.options.names}
            onChange={(v: string[]) => {
              checkedCx.resetChecked();
              updateFilters({
                names: v,
              });
            }}
            values={filters.names}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machine-models-machines"
            placeholder="common.machine"
            options={machineOptions}
            values={filters.machines}
            onChange={(machineIDs) => {
              checkedCx.resetChecked();
              updateFilters({
                machines: machineIDs,
              });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machine-models-types"
            placeholder="common.type"
            options={modelsCx.options.types.map((o) => ({
              label: o,
              value: o,
            }))}
            values={filters.types}
            onChange={(v) => {
              checkedCx.resetChecked();
              updateFilters({
                types: v as MachineModelType[],
              });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machine-models-statuses"
            placeholder="common.status"
            options={modelsCx.options.statuses.map((o) => ({
              label: o,
              value: o,
            }))}
            values={filters.statuses}
            onChange={(v) => {
              checkedCx.resetChecked();
              updateFilters({
                statuses: v as ModelStatus[],
              });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machine-models-ball-types"
            placeholder="common.ball-type"
            options={modelsCx.options.ballTypes.map((o) => ({
              label: o,
              value: o,
            }))}
            values={filters.ballTypes}
            onChange={(v) => {
              checkedCx.resetChecked();
              updateFilters({
                ballTypes: v as BallType[],
              });
            }}
            multiple
            optional
          />
        </Box>
        <Box>
          <CommonSearchInput
            id="machine-models-created"
            placeholder="common.date-added"
            options={modelsCx.options._created.map((o) => ({
              label: o,
              value: o,
            }))}
            values={filters.created}
            onChange={(v) => {
              checkedCx.resetChecked();
              updateFilters({
                created: v,
              });
            }}
            multiple
            reverseSort
            optional
          />
        </Box>
        <Box>
          <CommonSelectInput
            id="machine-models-inv-notes"
            placeholder="Investigation Notes"
            options={boolOptions}
            value={filters.hasInvestigationNotes?.toString()}
            onOptionalBooleanChange={(v) => {
              checkedCx.resetChecked();
              updateFilters({
                hasInvestigationNotes: v,
              });
            }}
            skipSort
            optional
          />
        </Box>
      </Grid>
      <Flex align="center" gap={RADIX.FLEX.GAP.SM}>
        <CommonTableButton
          color={RADIX.COLOR.NEUTRAL}
          icon={<UpdateIcon />}
          label="common.refresh"
          disabled={modelsCx.loading}
          variant="soft"
          onClick={() => {
            checkedCx.resetChecked();
            modelsCx.refresh();
          }}
        />

        <CommonTableButton
          icon={<ArchiveIcon />}
          label="common.archive-unused"
          color={RADIX.COLOR.WARNING}
          disabled={modelsCx.loading}
          onClick={onArchiveUnused}
          loading={archivingUnused}
          variant="soft"
        />

        <CommonTableButton
          icon={<PlusIcon />}
          label="common.create"
          color={RADIX.COLOR.SUCCESS}
          disabled={modelsCx.loading}
          onClick={() => openDialog(MachineModelsDialog.DialogCreate)}
        />
      </Flex>
    </Flex>
  );
};
